import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Alert, AlertDescription, AlertTitle } from "../../app/components/ui/alert";
import { Button } from "../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { Input } from "../../app/components/ui/input";
import { Label } from "../../app/components/ui/label";
import { useAuth } from "../../providers/AuthProvider";

export function LoginPage() {
  const { login, business } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = useMemo(() => email.trim() && password, [email, password]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await login(email, password);
      if (business && business.status !== "verified") {
        navigate("/pending-verification", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      const anyErr = err as { code?: string; message?: string };
      if (anyErr?.code === "BUSINESS_NOT_VERIFIED") {
        navigate("/pending-verification", { replace: true });
        return;
      }
      if (anyErr?.code === "PROFILE_INCOMPLETE") {
        navigate("/onboarding/business-profile", { replace: true });
        return;
      }
      setError(anyErr?.message ?? "Login failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-6">
      <Card className="w-full max-w-md border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold">Sign in</CardTitle>
          <CardDescription>Access your UpLevo POS account.</CardDescription>
        </CardHeader>
        <CardContent>
          {error ? (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Sign-in failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button className="w-full" type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-4 text-sm text-muted-foreground">
            New here?{" "}
            <Link to="/register" className="text-primary hover:underline">
              Create an account
            </Link>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-muted-foreground">
            <div>
              <div className="font-medium text-foreground">Mock super admin</div>
              <div>Email: admin@uplevo.test</div>
              <div>Password: Admin123!</div>
            </div>
            <div className="sm:text-right">
              <div className="font-medium text-foreground">Mock business owner</div>
              <div>Email: owner@uplevo.test</div>
              <div>Password: Owner123!</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

